// The copyable Claude instructions (the claude.ai connector's custom instructions). Session AHG built it on
// Settings, Integrations; Session CA1 (23 October 2026, the Director's brief) MOVED it here so the SAME card leads
// the Connected AI page as well. One component, two homes: Settings renders it inside its admin-only Integrations
// section, and the Connected AI page renders it first under the page heading, for an admin only (the route it
// reads, GET /api/settings/mcp-manifest, is adminOnly; neither home loosens that).
//
// Admin-only, READ ONLY: nothing here is editable and nothing is a secret.
//
// WHY IT EXISTS. The "Guvnor" connection on claude.ai carries a custom-instructions block that a person pastes in by
// hand, because no API can push it (RULE 4c). Every session that changes the tool surface leaves the pasted text
// stale, and until AHG the only way to see what was current was to read the repo. The text takes its tool count from
// the live registry, so the count cannot go stale; the version tells the Director at a glance whether what is in
// claude.ai still matches. The tools are NOT listed out in it (Session AHM, Director decision): the connection
// advertises them to claude.ai itself, in fuller detail than a truncated line could carry.
//
// It is the WHOLE instruction block, not a tool list on its own, and that is deliberate: pasting a bare tool list
// into the connector would replace a dozen sessions' worth of curated guidance and quietly make the assistant worse
// rather than more current.
//
// THE CALM CARD (Session CA1, house rule 11: one primary action, the rest folded). At rest the card is the heading
// "Claude instructions: version N", ONE Copy button and one line saying when this device last copied them. The full
// text sits behind "Show the instructions". When this device's last copy is OLDER than the current version, or this
// device has never copied them (decision D-CA1-1, default: show it), ONE amber line says so. The warnings about the
// document itself (fallback, a tool the guidance never explains, a count it no longer states) stay visible: each is a
// reason NOT to paste, which must never be folded away.
//
// PASTE ONCE (Session P3, the Director's decision of 24 October 2026). The guidance is now served by the read tool
// get_guvnor_guidance, so the Project needs only ONE fixed line telling Claude to call it (GUIDANCE_BOOTSTRAP_LINE on the
// server, returned here as bootstrapLine), which carries no version and never goes out of date. That line is now the
// card's one primary action, "Paste this once", above everything else. The full text stays as the FALLBACK, folded
// behind "Show the full instructions (fallback)" with its own secondary copy button, for a Project whose Claude does
// not call the tool. The amber out-of-date line (D-P3-1) now speaks only to a device that copied the FULL text before
// and has not copied the one-time line since: a device that has never copied anything is no longer nagged.
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import type { McpManifest } from "../lib/api";
import { copyText } from "../lib/clipboard";
import { useSingleTimeout } from "../lib/useSingleTimeout";
import { formatDateTime } from "../lib/format";
import { Button } from "./Button";
import { Loader } from "./Loader";
import { StatusPill } from "./StatusPill";

// Where "last copied" is remembered (Session AJA). Deliberately client-side only: this is a reminder for whoever is
// sitting at THIS browser, not an audit trail, and a per-device signal said honestly beats a shared one implied to be
// more than it is. The key and the record's shape are unchanged by CA1, so a copy recorded before CA1 still counts.
export const LAST_COPIED_KEY = "ele-mcp-manifest-last-copied";

export interface LastCopied {
  revision: string | null;
  at: string;
}

/** Every read of the device's record is wrapped: storage can be missing, blocked or throw (a private window,
 *  blocked site data), and the card must render the same without it. */
export function readLastCopied(): LastCopied | null {
  try {
    const raw = window.localStorage.getItem(LAST_COPIED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LastCopied>;
    return typeof parsed.at === "string" ? { revision: parsed.revision ?? null, at: parsed.at } : null;
  } catch {
    return null;
  }
}

function writeLastCopied(record: LastCopied): void {
  try {
    window.localStorage.setItem(LAST_COPIED_KEY, JSON.stringify(record));
  } catch {
    /* localStorage unavailable (private browsing, quota); the in-memory state still updates */
  }
}

/** "v300" -> 300. The revision marker in docs/MCP_SYSTEM_PROMPT.md is `**Revision vNN**` (mcpManifest.ts REVISION),
 *  so anything else is not a version this card can compare. */
export function revisionNumber(revision: string | null | undefined): number | null {
  const m = /^v(\d+)$/.exec(revision ?? "");
  return m ? Number(m[1]) : null;
}

// Session P3: where this device remembers copying the one-time line. A key of its own, so a copy of the line never
// reads as a copy of the full text (or the other way round). Same rules: a per-device reminder, every access wrapped.
export const BOOTSTRAP_LAST_COPIED_KEY = "ele-mcp-bootstrap-last-copied";

export function readBootstrapCopiedAt(): string | null {
  try {
    const raw = window.localStorage.getItem(BOOTSTRAP_LAST_COPIED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at?: unknown };
    return typeof parsed.at === "string" ? parsed.at : null;
  } catch {
    return null;
  }
}

function writeBootstrapCopiedAt(at: string): void {
  try {
    window.localStorage.setItem(BOOTSTRAP_LAST_COPIED_KEY, JSON.stringify({ at }));
  } catch {
    /* localStorage unavailable; the in-memory state still updates */
  }
}

/** Decision D-P3-1 (Session P3, replacing D-CA1-1's "never copied counts as out of date"). The amber line is about the
 *  FULL text, which is now the fallback: it is out of date only when this device copied the full text, that copy names
 *  no readable version or a lower one than the current, AND the device has not copied the one-time line since (once
 *  the line is in the Project, Claude fetches the current guidance itself, so an old full copy no longer matters). A
 *  device that has copied nothing is not nagged. Never out of date when the current version cannot be read. */
export function instructionsOutOfDate(current: string | null, last: LastCopied | null, bootstrapCopiedAt: string | null = null): boolean {
  const now = revisionNumber(current);
  if (now === null) return false;
  if (!last) return false;
  if (bootstrapCopiedAt !== null && Date.parse(bootstrapCopiedAt) >= Date.parse(last.at)) return false;
  const was = revisionNumber(last.revision);
  return was === null || was < now;
}

export function ConnectorInstructionsCard({ refreshKey, className = "mt-5" }: { refreshKey?: number; className?: string } = {}) {
  const [manifest, setManifest] = useState<McpManifest | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [copied, setCopied] = useState(false);
  // Session APV: cleared if the card closes first, and re-armed by a second copy.
  const resetCopiedLater = useSingleTimeout();
  const [copyFailed, setCopyFailed] = useState(false);
  const [lastCopied, setLastCopied] = useState<LastCopied | null>(() => readLastCopied());
  const [open, setOpen] = useState(false);
  const textRef = useRef<HTMLTextAreaElement | null>(null);
  // Session P3: the one-time line's own copy state, kept apart from the full text's.
  const [lineCopied, setLineCopied] = useState(false);
  const resetLineCopiedLater = useSingleTimeout();
  const [lineCopyFailed, setLineCopyFailed] = useState(false);
  const [lineCopiedAt, setLineCopiedAt] = useState<string | null>(() => readBootstrapCopiedAt());
  const lineRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    let live = true;
    api.settings
      .mcpManifest()
      .then((m) => {
        if (!live) return;
        setManifest(m);
        setState("ready");
      })
      .catch(() => {
        if (live) setState("error");
      });
    return () => {
      live = false;
    };
  }, [refreshKey]);

  // A refused copy opens the text and selects it, so copying by hand is one keystroke away. Done after the render
  // that un-hides the box (a hidden textarea cannot take focus).
  useEffect(() => {
    if (copyFailed && open && textRef.current) {
      textRef.current.focus();
      textRef.current.select();
    }
  }, [copyFailed, open]);

  useEffect(() => {
    if (lineCopyFailed && lineRef.current) {
      lineRef.current.focus();
      lineRef.current.select();
    }
  }, [lineCopyFailed]);

  async function doCopy() {
    if (!manifest) return;
    const ok = await copyText(manifest.text);
    setCopied(ok);
    setCopyFailed(!ok);
    if (ok) {
      resetCopiedLater(() => setCopied(false), 2000);
      const record: LastCopied = { revision: manifest.docRevision, at: new Date().toISOString() };
      setLastCopied(record);
      writeLastCopied(record);
    } else {
      setOpen(true);
    }
  }

  async function doCopyLine() {
    if (!manifest?.bootstrapLine) return;
    const ok = await copyText(manifest.bootstrapLine);
    setLineCopied(ok);
    setLineCopyFailed(!ok);
    if (ok) {
      resetLineCopiedLater(() => setLineCopied(false), 2000);
      const at = new Date().toISOString();
      setLineCopiedAt(at);
      writeBootstrapCopiedAt(at);
    }
  }

  const fallback = manifest?.source === "fallback";
  // A server older than P3 sends no line (the moment of a deploy): the card is then the CA1 card, the full text the one
  // primary action and nothing folded behind the word fallback.
  const hasLine = typeof manifest?.bootstrapLine === "string" && manifest.bootstrapLine.length > 0;
  const version = revisionNumber(manifest?.docRevision);
  const heading = version !== null ? `Claude instructions: version ${version}` : "Claude instructions: version not found";
  const outOfDate =
    Boolean(manifest) &&
    !fallback &&
    (hasLine ? instructionsOutOfDate(manifest?.docRevision ?? null, lastCopied, lineCopiedAt) : instructionsOutOfDate(manifest?.docRevision ?? null, lastCopied));
  const showLabel = hasLine ? "Show the full instructions (fallback)" : "Show the instructions";
  const hideLabel = hasLine ? "Hide the full instructions" : "Hide the instructions";

  return (
    <section
      aria-labelledby="claude-instructions-heading"
      data-testid="claude-instructions-card"
      className={`${className} min-w-0 rounded-ele border border-ele-purple-accent bg-ele-surface p-5`.trim()}
    >
      <h2 id="claude-instructions-heading" className="mb-1 font-heading text-lg text-ele-blue">
        {state === "ready" && manifest ? heading : "Claude instructions"}
      </h2>

      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" || !manifest ? (
        <p role="alert" className="text-sm text-ele-strong">
          Could not load the connector instructions. Please try again in a moment.
        </p>
      ) : (
        <>
          {hasLine ? (
            // The card's one primary action (house rule 11): the fixed line, pasted once into the claude.ai Project.
            <div data-testid="claude-instructions-paste-once" className="mb-4 min-w-0">
              <h3 className="mb-1 font-heading text-base text-ele-strong">Paste this once</h3>
              <p className="mb-2 text-sm text-ele-body">
                After this, Claude fetches the current instructions itself. You only need to paste it once.
              </p>
              <label className="mb-1 block text-xs uppercase tracking-widest text-ele-blue" htmlFor="mcp-bootstrap-line">
                Line for the claude.ai project
              </label>
              <textarea
                id="mcp-bootstrap-line"
                ref={lineRef}
                readOnly
                rows={3}
                value={manifest.bootstrapLine}
                onFocus={(e) => e.currentTarget.select()}
                className="mb-2 w-full min-w-0 whitespace-pre-wrap break-words rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm leading-relaxed text-ele-strong"
              />
              <div className="flex flex-wrap items-center gap-3">
                {/* P3 fix round (review finding 4): when the guidance document cannot be read, the line would send Claude
                    to a tool that answers the unreadable error, so the copy is demoted to a ghost like the full copy. */}
                <Button variant={fallback ? "ghost" : "primary"} className="max-sm:w-full" onClick={() => void doCopyLine()}>
                  {lineCopied ? "Copied" : "Copy the line"}
                </Button>
              </div>
              <p className="mt-2 text-xs text-ele-faint" data-testid="claude-instructions-line-last-copied">
                {lineCopiedAt ? `Line copied on this device: ${formatDateTime(lineCopiedAt)}.` : "Line not copied on this device yet."}
              </p>
              {lineCopyFailed ? (
                <p role="alert" className="mt-2 text-sm text-ele-danger">
                  The browser would not give access to the clipboard. The line is selected above: copy it by hand.
                </p>
              ) : null}
            </div>
          ) : null}

          {outOfDate && version !== null ? (
            // role status, not alert: a standing reminder, not an interruption (D-CA1-2). The words are the CA1 brief's,
            // with the one-time line offered as the way out once there is one (D-P3-2).
            <p
              role="status"
              data-testid="claude-instructions-out-of-date"
              className="mb-3 rounded-ele border border-ele-amber-line bg-ele-amber-wash px-3 py-2 text-sm text-ele-amber-wash-ink"
            >
              Your Claude instructions are out of date: copy version {version} and paste it into the project
              {hasLine ? ", or paste the line above once instead" : ""}
            </p>
          ) : null}

          {/* A fallback block is NOT a fresher copy of the instructions, it is a much thinner one, so pasting it would
              lose the guidance. These three stay outside the fold: each is a reason not to paste. */}
          {fallback ? (
            <p role="alert" className="mb-3 rounded-ele border border-amber-500 bg-amber-500/10 p-3 text-sm text-amber-300">
              The written guidance could not be loaded, so this is a tool list only. Do not paste this over what is in
              claude.ai now: it would remove the guidance rather than refresh it. Copy the full instructions from
              docs/MCP_SYSTEM_PROMPT.md instead.
            </p>
          ) : manifest.staleToolMentions.length > 0 ? (
            <p role="alert" className="mb-3 rounded-ele border border-amber-500 bg-amber-500/10 p-3 text-sm text-amber-300">
              docs/MCP_SYSTEM_PROMPT.md's changelog names{" "}
              {manifest.staleToolMentions.length === 1 ? "a tool" : `${manifest.staleToolMentions.length} tools`} the written
              guidance below never explains: {manifest.staleToolMentions.map((t) => `\`${t}\``).join(", ")}. This is exactly
              the v21-v24 bug that prompted this warning: real documentation was written but landed above the paste
              separator, where it never reaches claude.ai. Fix the document before relying on this paste.
            </p>
          ) : !manifest.promptCountSynced ? (
            <p role="alert" className="mb-3 rounded-ele border border-amber-500 bg-amber-500/10 p-3 text-sm text-amber-300">
              The written guidance no longer states a tool count in the wording this page looks for, so if it states one
              at all it has been left alone and may be wrong. The count shown on this page is taken from the live
              platform and is correct either way.
            </p>
          ) : null}

          {hasLine ? null : (
            <>
              <div className="mb-2 flex flex-wrap items-center gap-3">
                {/* Demoted to a quiet ghost in the fallback state: there, copying is the action the warning below is
                    telling the Director NOT to take, so it must not be the one green primary (the Session ABB precedent). */}
                <Button variant={fallback ? "ghost" : "primary"} className="max-sm:w-full" onClick={() => void doCopy()}>
                  {copied ? "Copied" : "Copy to clipboard"}
                </Button>
              </div>
              <FullCopyLastCopied fallback={fallback} lastCopied={lastCopied} current={manifest.docRevision} />
            </>
          )}

          {copyFailed ? (
            <p role="alert" className="mb-3 text-sm text-ele-danger">
              The browser would not give access to the clipboard. The instructions are shown and selected below: copy
              them by hand.
            </p>
          ) : null}

          <Button
            variant="ghost"
            size="sm"
            className="-ml-3"
            aria-expanded={open}
            aria-controls="claude-instructions-text"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? hideLabel : showLabel}
          </Button>

          {/* Kept in the page while folded (the hidden attribute), so aria-controls always names a real element. */}
          <div id="claude-instructions-text" hidden={!open} className="mt-3 min-w-0">
            {hasLine ? (
              <>
                <p className="mb-3 text-sm text-ele-body">
                  The fallback: if Claude in the project does not fetch the instructions itself, paste this whole text into
                  the project instead of the line above, and paste it again whenever the version moves on.
                </p>
                <div className="mb-2 flex flex-wrap items-center gap-3">
                  <Button variant="ghost" className="max-sm:w-full" onClick={() => void doCopy()}>
                    {copied ? "Copied" : "Copy the full instructions"}
                  </Button>
                </div>
                <FullCopyLastCopied fallback={fallback} lastCopied={lastCopied} current={manifest.docRevision} />
              </>
            ) : null}
            {/* Deliberately says what IS generated rather than "always current". The count is generated; the written
                guidance is a hand-maintained document, and a session can change what a tool DOES without touching it,
                which is the whole reason RULE 4c exists. */}
            <p className="mb-3 text-sm text-ele-body">
              The custom instructions for the Guvnor connection on claude.ai. The tool count is taken from the live platform
              and the written guidance around it is ELE&apos;s own hand-maintained document.{" "}
              {fallback
                ? null
                : "The individual tools are not listed here: claude.ai already shows what the connection can do, in more detail than would fit on this page. "}
              The version below is stamped into the text, so it can be compared with the last line of what is in claude.ai
              now.
            </p>
            <div className="mb-3">
              {/* ONE pill, ONE number (Session AJA), read straight off docs/MCP_SYSTEM_PROMPT.md's revision marker. */}
              <StatusPill
                word={`${manifest.docRevision ? `Guidance ${manifest.docRevision}` : "No revision found"} · ${manifest.toolCount} tools`}
                tone="blue"
              />
            </div>
            <label className="mb-1 block text-xs uppercase tracking-widest text-ele-blue" htmlFor="mcp-manifest-text">
              Custom instructions
            </label>
            <textarea
              id="mcp-manifest-text"
              ref={textRef}
              readOnly
              rows={14}
              value={manifest.text}
              onFocus={(e) => e.currentTarget.select()}
              className="w-full min-w-0 whitespace-pre-wrap break-words rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 font-mono text-xs leading-relaxed text-ele-strong"
            />
            {/* The reconnect sentence is the actual remedy for a SHORT tool list in claude.ai, which is a client-side
                cache or disclosure condition rather than anything a paste can fix. */}
            <p className="mt-2 text-xs text-ele-faint">
              {manifest.text.length.toLocaleString("en-GB")} characters. Claude is told which tools exist by the
              connection itself, not by this text{fallback ? "" : ", which is why the tools are not listed out here"}. If
              claude.ai appears to be missing tools, reconnect the connector: pasting this will not fix that.
            </p>
          </div>
        </>
      )}
    </section>
  );
}

// "Last copied" (Session AJA): whether THIS browser has a record of copying the full text, and when.
function FullCopyLastCopied({ fallback, lastCopied, current }: { fallback: boolean; lastCopied: LastCopied | null; current: string | null }) {
  return (
    <p className="mb-3 text-xs text-ele-faint" data-testid="claude-instructions-last-copied">
      {fallback
        ? null
        : lastCopied
          ? `Last copied on this device: ${formatDateTime(lastCopied.at)}${
              lastCopied.revision && lastCopied.revision !== current ? ` (version ${revisionNumber(lastCopied.revision) ?? lastCopied.revision})` : ""
            }.`
          : "Not copied on this device yet."}
    </p>
  );
}
